import React, { useContext, useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Form, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm, useWatch, FormProvider } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { JobService } from "./service";
import { CompanyService } from "../company/service";
import { JobTargetGroupsService } from "../jobTargetGroups/service";
import { JobCategoriesService } from "../jobCategories/service";
import { LocationService } from "../location/service";
import { QualificationsService } from "../qualifications/service";
import { JobFormatService } from "../jobFormat/service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import Select from 'react-select';
import { selectDataConverter } from "../../shared/utils/functions";
import { Editor } from "@tinymce/tinymce-react";
import { useTranslation } from "react-i18next";
import { EnumsContext } from "../../context/EnumsContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import login from "../../authentication/login/Login";
import moment from "moment";
import FullCalendars from "../../shared/calendar/FullCalendar";
import { AuthContext } from "../../context/AuthContext";
import { UserService } from "../user/service";

import FormErrorSummary from "../../shared/components/FormErrorSummary";

// Yup schema with duplicate check
// const timeWindowDaysSchema = yup.array()
//     .of(
//         yup.object().shape({
//             day: yup.string().required(),
//             startTime: yup.string().required(),
//             endTime: yup.string().required(),
//         })
//     )
//     .test(
//         "no-duplicates",
//         "Duplicate events with the same day, start time, and end time are not allowed.",
//         (value) => {
//             if (!value) return true;
//             const seen = new Set();
//             for (const item of value) {
//                 const key = `${item.day}_${item.startTime}_${item.endTime}`;
//                 if (seen.has(key)) return false;
//                 seen.add(key);
//             }
//             return true;
//         }
//     );

const schema = yup.object().shape({
    title: yup.string().required('formErrors.titleRequired'),
    description: yup.string().required('formErrors.descriptionRequired'),
    employmentType: yup.string().required('formErrors.employmentTypeRequired'),
    location: yup.number().when('locationType', {
        is: (locationType: string) => locationType !== 'online',
        then: (schema) => schema.required('formErrors.locationRequired').typeError('formErrors.locationRequired'),
        otherwise: (schema) => schema.nullable(),
    }),
    category: yup.number().required('formErrors.categoryRequired').typeError('formErrors.categoryRequired'),
    targetGroup: yup.number().required('formErrors.targetGroupRequired').typeError('formErrors.targetGroupRequired'),
    company: yup.number().required('formErrors.companyRequired').typeError('formErrors.companyRequired'),
    qualifications: yup.array().of(yup.number().required()).default([]), // Optional - not required in frontend
    locationType: yup.string().required('formErrors.locationTypeRequired'),
    format: yup.number().required('formErrors.jobFormatRequired').typeError('formErrors.jobFormatRequired'),
    pricingMethod: yup.string().required('formErrors.pricingMethodRequired'),
    fee: yup.number().required('formErrors.feeRequired').typeError('formErrors.feeRequired'),
    specialNeeds: yup.string(), // Optional - not required in frontend
    applicationDeadline: yup.date().required('formErrors.applicationDeadlineRequired').typeError('formErrors.applicationDeadlineRequired'),
    lectureTimeWindowType: yup.string().required('formErrors.lectureTimeWindowTypeRequired'),
    startDate: yup.date().when('lectureTimeWindowType', {
        is: (type: string) => type !== 'open',
        then: (schema) => schema.required('formErrors.startDateRequired'),
        otherwise: (schema) => schema.nullable(),
    }),
    endDate: yup.date().when('lectureTimeWindowType', {
        is: (type: string) => type !== 'open',
        then: (schema) => schema.required('formErrors.endDateRequired'),
        otherwise: (schema) => schema.nullable(),
    }),
    timeWindowDescription: yup.string().when('lectureTimeWindowType', {
        is: 'alreadyDefined',
        then: (schema) => schema.required('formErrors.timeWindowDescriptionRequired'),
        otherwise: (schema) => schema.nullable(),
    }),
    responsiblePerson: yup.number().required('formErrors.responsiblePersonRequired').typeError('formErrors.responsiblePersonRequired'),
});

interface JobState {
    title: string;
    description: string;
    employmentType: string;
    location?: number; // Optional when locationType is online
    category: number;
    targetGroup: number;
    company: number;
    qualifications: number[];
    locationType: string;
    format: number;
    pricingMethod: string;
    fee: number;
    specialNeeds?: string; // Optional
    applicationDeadline: Date;
    lectureTimeWindowType: string;
    startDate?: Date;
    endDate?: Date;
    timeWindowDescription?: string;
    timeWindowDays?: any[]
    responsiblePerson: number; // Required
}
type FormTimes = {
    [day: string]: {
        fromTime: Date | null;
        toTime: Date | null;
    };
};
const JobForm: React.FC = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const isEdit = Boolean(id);
    const [jobData, setJobData] = useState<JobState | null>(null);
    const [companyList, setCompanyList] = useState<any[]>([]);
    const [targetGroupList, setTargetGroupList] = useState<any[]>([]);
    const [qualificationsList, setQualificationsList] = useState<any[]>([]);
    const [jobFormatList, setJobFormatList] = useState<any[]>([]);
    const navigate = useNavigate();
    const { control, handleSubmit, reset, setValue, watch, formState: { errors }, getValues } = useForm<JobState>({
        resolver: yupResolver(schema),
        defaultValues: {
            title: "",
            description: "",
            employmentType: "",
            location: undefined,
            category: undefined,
            targetGroup: undefined,
            company: undefined,
            qualifications: [],
            locationType: "",
            format: undefined,
            pricingMethod: "",
            fee: undefined,
            specialNeeds: "",
            applicationDeadline: undefined,
            lectureTimeWindowType: "",
            startDate: undefined,
            endDate: undefined,
            timeWindowDescription: "",
            timeWindowDays: [],
            responsiblePerson: 0, // Required field
        }
    });
    const auth = useContext(AuthContext);
    console.log(auth, 'auth context')
    const lectureTimeWindowTypeValue = watch("lectureTimeWindowType");
    const startDate = watch("startDate");
    const endDate = watch("endDate");
    const locationType = watch("locationType");
    const { enums, loading } = useContext(EnumsContext);
    const workingDays = enums.workingDay
    const jobLocationType = enums.jobLocationType?.map((item: any) => ({
        label: item[0].toUpperCase() + item.slice(1),
        value: item
    }));
    const employmentTypeOptions = (enums.employmentType || ["all", "employed", "freelance"]).map((item: any) => ({
        label: t(`enums.employmentType.${item}`),
        value: item
    }));
    const pricingMethod = enums.pricingMethod?.map((item: any) => ({
        label: item[0].toUpperCase() + item.slice(1).replaceAll('_', ' '),
        value: item
    }));
    const lectureTimeWindowType = enums.lectureTimeWindowType?.map((item: any) => ({
        label: item[0].toUpperCase() + item.slice(1).replaceAll('_', ' '),
        value: item
    }));
    const [mainCategoryList, setMainCategoryList] = useState<any[]>([]);
    const [secondDropdownOptions, setSecondDropdownOptions] = useState<any[]>([]);
    const [thirdDropdownOptions, setThirdDropdownOptions] = useState<any[]>([]);
    const [selectedMainCategory, setSelectedMainCategory] = useState<number | null>(null);
    const [selectedSecondCategory, setSelectedSecondCategory] = useState<number | null>(null)
    const [mainLocationList, setMainLocationList] = useState<any[]>([]); // Level 1 locations
    const [secondLocationOptions, setSecondLocationOptions] = useState<any[]>([]); // Level 2 locations
    const [selectedMainLocation, setSelectedMainLocation] = useState<number | null>(null); // Selected level 1 location
    const [showFullCalendar, setShowFullCalendar] = useState(false);
    const timeWindowDays = useWatch({ control, name: "timeWindowDays" }) || [];
    const [selectedDays, setSelectedDays] = useState<string[]>([]);
    const [formTimes, setFormTimes] = useState<FormTimes>({});
    const [companyUsers, setCompanyUsers] = useState<any[]>([]);
    const [responsiblePersonDisabled, setResponsiblePersonDisabled] = useState(true);



    useEffect(() => {
        if (isEdit) {
            const fetchJob = async () => {
                try {
                    const response = await JobService.getSingle(+id!);
                    const job = response;

                    setJobData({
                        title: job.title,
                        description: job.description,
                        employmentType: job.employmentType,
                        location: job.location?.id,
                        category: job.category?.id,
                        targetGroup: job.targetGroup?.id,
                        company: job.company?.id,
                        qualifications: job.qualifications.map((qual: any) => qual?.id),
                        locationType: job.locationType,
                        format: job.format?.id,
                        pricingMethod: job.pricingMethod,
                        fee: job.fee,
                        specialNeeds: job.specialNeeds,
                        applicationDeadline: new Date(job.applicationDeadline),
                        lectureTimeWindowType: job.lectureTimeWindowType,
                        startDate: job.startDate ? moment(job.startDate, "YYYY-MM-DD").toDate() : undefined,
                        endDate: job.endDate ? moment(job.endDate, "YYYY-MM-DD").toDate() : undefined,
                        timeWindowDescription: job.timeWindowDescription || "",
                        timeWindowDays: job.timeWindowDays || [],
                        responsiblePerson: job.responsiblePerson.id || null
                    });

                    if (job.locationType) {
                        setValue("locationType", job.locationType);
                    }


                    if (job.location) {
                        const mainLocation = mainLocationList.find((mainLoc) =>
                            mainLoc.children?.some((secondLoc: any) =>
                                secondLoc.children?.some((thirdLoc: any) => thirdLoc.id === job.location.id) ||
                                secondLoc.id === job.location.id
                            )
                        );

                        if (mainLocation) {
                            setSelectedMainLocation(mainLocation.value);

                            const secondLevelLocations = mainLocation.children || [];
                            const options = secondLevelLocations.flatMap((secondLevel: any) => [
                                {
                                    label: <span
                                        style={{ color: "#aaaaaa", fontWeight: "bold" }}>{secondLevel.name}</span>,
                                    value: secondLevel.id,
                                    isDisabled: true,
                                },
                                ...(secondLevel.children || []).map((thirdLevel: any) => ({
                                    label: thirdLevel.name,
                                    value: thirdLevel.id,
                                })),
                            ]);
                            setSecondLocationOptions(options);


                            setValue("location", job.location.id);
                        }
                    }

                    if (job.category && !!mainCategoryList?.length) {
                        const selectedCategory = job?.category;
                        const level = selectedCategory?.level;

                        // Determine the main category based on the selected category's level
                        let mainCategory: any = null;
                        if (level === 3) {
                            mainCategory = selectedCategory?.parent?.parent;
                        } else if (level === 2) {
                            mainCategory = selectedCategory?.parent;
                        } else {
                            // level 1 (or undefined) - the selected category IS the main category
                            mainCategory = selectedCategory;
                        }

                        const combinedMainCategory = mainCategoryList?.find((category) => category?.value === mainCategory?.id);
                        if (combinedMainCategory) {
                            setSelectedMainCategory(combinedMainCategory.value);

                            const secondLevelCategories = (combinedMainCategory.children || []).map((category: any) => ({
                                label: category?.title,
                                value: category?.id,
                                children: category?.children
                            }));
                            setSecondDropdownOptions(secondLevelCategories);

                            // Determine which id should be selected as second level (if any)
                            const selectedSecondLevelId = level === 3
                                ? selectedCategory?.parent?.id
                                : (level === 2 ? selectedCategory?.id : undefined);

                            const nestedDropdown = secondLevelCategories.find(
                                (category: { value: number }) => category.value === selectedSecondLevelId
                            )?.children;

                            setThirdDropdownOptions(
                                (nestedDropdown || []).map((category: any) => ({
                                    label: category?.title,
                                    value: category?.id
                                }))
                            );

                            setSelectedSecondCategory(selectedSecondLevelId || null);
                            setValue("category", selectedCategory.id);
                        }
                    }

                } catch (error) {
                    ErrorToast(error);
                }
            };

            fetchJob();
        }
    }, [isEdit, id, mainLocationList, mainCategoryList, reset, setValue]);
    useEffect(() => {
        if (jobData) {
            reset(jobData);
        }
    }, [jobData, reset]);

    useEffect(() => {
        CompanyService.getAll({ perPage: 0, page: 1 }).then((res) => {
            setCompanyList(res.data.map((comp: any) => ({ label: comp.name, value: comp.id })));
        }).catch((err) => ErrorToast(err));

        JobCategoriesService.getAll().then((res) => {
            setMainCategoryList(res.map((cat: any) => ({ label: cat.title, value: cat.id, children: cat.children })));
        }).catch((err) => ErrorToast(err));

        JobTargetGroupsService.getAll({ perPage: 0, page: 1 }).then((res) => {
            setTargetGroupList(selectDataConverter(res.data, 'title', 'id'));
        }).catch((err) => ErrorToast(err));

        QualificationsService.getAll({ perPage: 0, page: 1 }).then((res) => {
            setQualificationsList(selectDataConverter(res.data, 'name', 'id'));
        }).catch((err) => ErrorToast(err));

        JobFormatService.getAll({ perPage: 0, page: 1 }).then((res) => {
            setJobFormatList(selectDataConverter(res.data, "title", "id"));
        }).catch((err) => ErrorToast(err));
    }, []);


    useEffect(() => {
        LocationService.getTree({ perPage: 0, page: 1 }).then((res) => {
            setMainLocationList(
                res.map((loc: any) => ({
                    label: loc.name,
                    value: loc.id,
                    children: loc.children || [],
                }))
            );
        }).catch((err) => ErrorToast(err));
    }, []);
    console.log(errors)

    useEffect(() => {
        if (selectedMainLocation) {
            const mainLocation = mainLocationList.find((loc) => loc.value === selectedMainLocation);
            if (mainLocation && mainLocation.children) {
                const options = mainLocation.children.flatMap((secondLevel: any) => [
                    {
                        label: <span style={{ color: "#aaaaaa", fontWeight: "bold" }}>{secondLevel.name}</span>,
                        value: secondLevel.id,
                        isDisabled: true,
                    },
                    ...(secondLevel.children || []).map((thirdLevel: any) => ({
                        label: thirdLevel.name,
                        value: thirdLevel.id,
                    })),
                ]);
                setSecondLocationOptions(options);
            } else {
                setSecondLocationOptions([]);
            }
        } else {
            setSecondLocationOptions([]);
        }
    }, [selectedMainLocation, mainLocationList]);
    // const handleCheckboxChange = (day: string) => {
    //     if (selectedDays.includes(day)) {
    //         // Remove from selectedDays and timeWindowDays
    //         setSelectedDays(prev => prev.filter(d => d !== day));
    //         setValue(
    //           "timeWindowDays",
    //           timeWindowDays.filter((item: any) => item.day !== day)
    //         );
    //       } else {
    //         // Add to selectedDays and timeWindowDays
    //         setSelectedDays(prev => [...prev, day]);
    //         setValue("timeWindowDays", [
    //           ...timeWindowDays,
    //           {
    //             day,
    //             startTime: "",
    //             endTime: "",
    //           },
    //         ]);
    //       }
    // };

    // const handleTimeChange = (
    //   day: string,
    //   type: "startTime" | "endTime",
    //   value: Date | null
    // ) => {
    //   setValue(
    //     "timeWindowDays",
    //     timeWindowDays.map((item) =>
    //       item.day === day
    //         ? {
    //             ...item,
    //             [type]: value ? moment(value).format("HH:mm") : "",
    //             day, // ensure the day is set correctly
    //           }
    //         : item
    //     )
    //   );
    // };

    const onSubmit = async (data: JobState) => {

        // Exclude location field when locationType is online
        const { location, ...baseData } = data;

        const payload = {
            ...baseData,
            // Only include location if locationType is not online
            ...(data.locationType !== 'online' && location !== undefined && { location }),
            startDate: data.startDate ? moment(data.startDate).format("YYYY-MM-DD") : undefined,
            endDate: data.endDate ? moment(data.endDate).format("YYYY-MM-DD") : undefined,

        };

        // Debug log to verify location is not included when online
        console.log('Payload being sent:', payload);
        console.log('Location type:', data.locationType);
        console.log('Location value:', location);


        try {
            isEdit
                ? await JobService.update(payload, +id!)
                : await JobService.create(payload);

            SuccessToast(t(`job.${isEdit ? 'update' : 'create'}`));
            navigate('/jobs');
            reset();
        } catch (error) {
            ErrorToast(error);
        }
    };

    useEffect(() => {
        if (
            watch("lectureTimeWindowType") === "self_selectable" &&
            startDate &&
            endDate
        ) {
            setShowFullCalendar(true);
        } else {
            setShowFullCalendar(false);
        }
    }, [watch("lectureTimeWindowType"), startDate, endDate]);

    useEffect(() => {
        if (jobData && jobData.location) {
            const mainLocation = mainLocationList.find((mainLoc) =>
                mainLoc.children?.some((secondLoc: any) =>
                    secondLoc.children?.some((thirdLoc: any) => thirdLoc.id === jobData.location) || secondLoc.id === jobData.location
                )
            );

            if (mainLocation) {
                setSelectedMainLocation(mainLocation.value);

                const secondLevelLocations = mainLocation.children || [];
                const options = secondLevelLocations.flatMap((secondLevel: any) => [
                    {
                        label: <span style={{ color: "#aaaaaa", fontWeight: "bold" }}>{secondLevel.name}</span>,
                        value: secondLevel.id,
                        isDisabled: true,
                    },
                    ...(secondLevel.children || []).map((thirdLevel: any) => ({
                        label: thirdLevel.name,
                        value: thirdLevel.id,
                    })),
                ]);
                setSecondLocationOptions(options);
            }
        }
    }, [jobData, mainLocationList]);




    // Watch company field
    const selectedCompany = watch("company");

    useEffect(() => {
        if (selectedCompany) {
            setResponsiblePersonDisabled(false);
            console.log("Fetching company users for:", selectedCompany);
            UserService.getAll({ company: selectedCompany }).then((res) => {

                setCompanyUsers(res.data.map((user: any) => ({
                    label: user.firstName + ' ' + user.lastName || user.email,
                    value: user.id
                })));
                // Prefill with auth.user.id if not set
                if (!getValues("responsiblePerson") && auth?.user?.id) {
                    setValue("responsiblePerson", auth.user.id);
                }
            }).catch((err) => ErrorToast(err));
        } else {
            setCompanyUsers([]);
            setResponsiblePersonDisabled(true);
            setValue("responsiblePerson", 0); // Reset to default value for required field
        }
    }, [selectedCompany, setValue, getValues, auth]);

    // When editing, prefill responsiblePerson if present
    useEffect(() => {
        if (jobData) {
            console.log(jobData, 'jobdataaa')
            if (jobData.responsiblePerson) {
                setValue("responsiblePerson", (jobData.responsiblePerson as any).id);
            } else if (auth?.user?.id) {
                setValue("responsiblePerson", auth.user.id);
            }
        }
    }, [jobData, setValue, auth]);

    // Clear location when online is selected
    useEffect(() => {
        if (locationType === 'online') {
            // Completely clear location field and related state
            setValue("location", undefined, { shouldValidate: true });
            setSelectedMainLocation(null);
            setSecondLocationOptions([]);
        }
    }, [locationType, setValue]);

    if (loading) {
        return <div className="">Loading...</div>
    }

    return (
        <React.Fragment>
            <Row>
                <Col sm={12}>
                    <Card>
                        <CardBody>
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <FormErrorSummary errors={errors} />
                                <Row className="g-3">
                                    <Col sm={12} xl={4}>
                                        <div className="mb-0">
                                            <label className="form-label">{t("job.title")} <span className="text-danger">*</span></label>
                                            <Controller
                                                name="title"
                                                control={control}
                                                render={({ field }) => (
                                                    <input
                                                        type="text"
                                                        className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                                                        placeholder={t("job.enterTitle")}
                                                        {...field}
                                                    />
                                                )}
                                            />
                                            {errors.title &&
                                                <div className="invalid-feedback">
                                                    {t(errors.title.message as string)}
                                                </div>}
                                        </div>
                                    </Col>
                                    {/* <Col sm={12} xl={4}>
                                        <div className="mb-0">
                                            <label className="form-label">{t("job.location")}</label>
                                            <Controller
                                                name="location"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        id="location"
                                                        classNamePrefix="react-select"
                                                        className={`react-select-container ${errors.location ? 'is-invalid' : ''}`}
                                                        value={locationList.find(loc => { return loc.value === field.value })}
                                                        onChange={(selectedOption) => field.onChange(selectedOption ? selectedOption.value : null)}
                                                        options={locationList}
                                                    />
                                                )}
                                            />
                                            {errors.location && <div className="invalid-feedback">{errors.location.message}</div>}
                                        </div>
                                    </Col> */}
                                    <Col sm={12} xl={4}>
                                        <div className="mb-0">
                                            <label className="form-label">{t("job.locationType")} <span className="text-danger">*</span></label>
                                            <Controller
                                                name="locationType"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        id="locationType"
                                                        classNamePrefix="react-select"
                                                        className={`react-select-container ${errors.locationType ? "is-invalid" : ""}`}
                                                        value={jobLocationType?.find((locType: any) => {
                                                            return locType.value === field.value
                                                        })}
                                                        onChange={(selectedOption) => field.onChange(selectedOption ? selectedOption.value : null)}
                                                        options={jobLocationType}
                                                    />
                                                )}
                                            />
                                            {errors.locationType &&
                                                <div className="invalid-feedback">
                                                    {t(errors.locationType.message as string)}
                                                </div>}
                                        </div>
                                    </Col>
                                    <Col sm={12} xl={4}>
                                        <div className="mb-0">
                                            <label className="form-label">{t("job.employmentType")} <span className="text-danger">*</span></label>
                                            <Controller
                                                name="employmentType"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        id="employmentType"
                                                        classNamePrefix="react-select"
                                                        className={`react-select-container ${errors.employmentType ? "is-invalid" : ""}`}
                                                        value={employmentTypeOptions?.find((opt: any) => opt.value === field.value)}
                                                        onChange={(selectedOption) => field.onChange(selectedOption ? selectedOption.value : null)}
                                                        options={employmentTypeOptions}
                                                    />
                                                )}
                                            />
                                            {errors.employmentType &&
                                                <div className="invalid-feedback">
                                                    {t(errors.employmentType.message as string)}
                                                </div>}
                                        </div>
                                    </Col>
                                    <Col sm={12} xl={4}>
                                        <div className="mb-0">
                                            <label className="form-label">{t("job.targetGroup")} <span className="text-danger">*</span></label>
                                            <Controller
                                                name="targetGroup"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        id="targetGroup"
                                                        classNamePrefix="react-select"
                                                        className={`react-select-container ${errors.targetGroup ? 'is-invalid' : ''}`}
                                                        value={targetGroupList.find(tg => tg.value === field.value)}
                                                        onChange={(selectedOption) => field.onChange(selectedOption ? selectedOption.value : null)}
                                                        options={targetGroupList}
                                                    />
                                                )}
                                            />
                                            {errors.targetGroup &&
                                                <div className="invalid-feedback">
                                                    {t(errors.targetGroup.message as string)}
                                                </div>}
                                        </div>
                                    </Col>
                                    <Col sm={12} xl={3}>
                                        <div className="mb-0">
                                            <label className="form-label">{t("job.company")} <span className="text-danger">*</span></label>
                                            <Controller
                                                name="company"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        id="company"
                                                        classNamePrefix="react-select"
                                                        className={`react-select-container ${errors.company ? 'is-invalid' : ''}`}
                                                        value={companyList.find(company => company.value === field.value)}
                                                        onChange={(selectedOption) => field.onChange(selectedOption ? selectedOption.value : null)}
                                                        options={companyList}
                                                    />
                                                )}
                                            />
                                            {errors.company &&
                                                <div className="invalid-feedback">
                                                    {t(errors.company.message as string)}
                                                </div>}
                                        </div>
                                    </Col>
                                    {selectedCompany && (
                                        <Col sm={12} xl={3}>
                                            <div className="mb-0">
                                                <label className="form-label">{t("job.responsiblePerson")} <span className="text-danger">*</span></label>
                                                <Controller
                                                    name="responsiblePerson"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Select
                                                            id="responsiblePerson"
                                                            classNamePrefix="react-select"
                                                            className={`react-select-container ${errors.responsiblePerson ? 'is-invalid' : ''}`}
                                                            isDisabled={responsiblePersonDisabled}
                                                            options={companyUsers}
                                                            value={companyUsers.find(user => user.value === field.value) || null}
                                                            onChange={(selectedOption: any) => field.onChange(selectedOption ? selectedOption.value : null)}
                                                            placeholder={responsiblePersonDisabled ? t("job.selectCompanyFirst") : t("job.selectResponsiblePerson")}
                                                        />
                                                    )}
                                                />
                                                {errors.responsiblePerson &&
                                                    <div className="invalid-feedback d-block">
                                                        {t(errors.responsiblePerson.message as string)}
                                                    </div>}
                                            </div>
                                        </Col>
                                    )}

                                    <Col sm={12} xl={3}>
                                        <div className="mb-0">
                                            <label className="form-label">{t("job.qualifications")}</label>
                                            <Controller
                                                name="qualifications"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        id="qualifications"
                                                        classNamePrefix="react-select"
                                                        className={`react-select-container ${errors.qualifications ? 'is-invalid' : ''}`}
                                                        value={qualificationsList.filter(qual => field.value.includes(qual.value))}
                                                        onChange={(selectedOptions) => field.onChange(selectedOptions ? selectedOptions.map((option: any) => option.value) : [])}
                                                        options={qualificationsList}
                                                        isMulti
                                                    />
                                                )}
                                            />
                                            {errors.qualifications &&
                                                <div className="invalid-feedback">
                                                    {t(errors.qualifications.message as string)}
                                                </div>}
                                        </div>
                                    </Col>
                                    <Col sm={12} xl={3}>
                                        <div className="mb-0">
                                            <label className="form-label">{t("job.format")} <span className="text-danger">*</span></label>
                                            <Controller
                                                name="format"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        id="format"
                                                        classNamePrefix="react-select"
                                                        className={`react-select-container ${errors.format ? "is-invalid" : ""}`}
                                                        value={jobFormatList.find((format: any) => format.value === field.value)}
                                                        onChange={(selectedOption) => field.onChange(selectedOption ? selectedOption.value : null)}
                                                        options={jobFormatList}
                                                    />
                                                )}
                                            />
                                            {errors.format &&
                                                <div className="invalid-feedback">
                                                    {t(errors.format.message as string)}
                                                </div>}
                                        </div>
                                    </Col>
                                    <Col sm={12} xl={3}>
                                        <div className="mb-0">
                                            <label className="form-label">{t("job.pricingMethod")} <span className="text-danger">*</span></label>
                                            <Controller
                                                name="pricingMethod"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        id="pricingMethod"
                                                        classNamePrefix="react-select"
                                                        className={`react-select-container ${errors.pricingMethod ? "is-invalid" : ""}`}
                                                        value={pricingMethod?.find((opt: any) => opt.value === field.value)}
                                                        onChange={(selectedOption) => field.onChange(selectedOption ? selectedOption.value : null)}
                                                        options={pricingMethod}
                                                    />
                                                )}
                                            />
                                            {errors.pricingMethod &&
                                                <div className="invalid-feedback">
                                                    {t(errors.pricingMethod.message as string)}
                                                </div>}
                                        </div>
                                    </Col>

                                    <Col sm={12} xl={3}>
                                        <div className="mb-0">
                                            <label className="form-label">{t("job.fee")} <span className="text-danger">*</span></label>
                                            <Controller
                                                name="fee"
                                                control={control}
                                                render={({ field }) => (
                                                    <input
                                                        type="number"
                                                        className={`form-control ${errors.fee ? "is-invalid" : ""}`}
                                                        placeholder={t("job.enterFee")}
                                                        {...field}
                                                    />
                                                )}
                                            />
                                            {errors.fee && <div className="invalid-feedback">
                                                {t(errors.fee.message as string)}
                                            </div>}
                                        </div>
                                    </Col>
                                    <Col sm={6} xl={3}>
                                        <div className="mb-0 d-flex flex-column">
                                            <label className="form-label">{t("job.applicationDeadline")} <span className="text-danger">*</span></label>
                                            <div>
                                                <Controller
                                                    name="applicationDeadline"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <DatePicker
                                                            selected={field.value ? new Date(field.value) : null}
                                                            onChange={(date: Date | null) => field.onChange(date)}
                                                            dateFormat="dd.MM.yyyy"
                                                            className={`form-control ${errors.applicationDeadline ? 'is-invalid' : ''}`}
                                                            placeholderText={t("job.applicationDeadline")}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            {errors.applicationDeadline && <div className="invalid-feedback">
                                                {t(errors.applicationDeadline.message as string)}
                                            </div>}
                                        </div>
                                    </Col>

                                    <Col sm={12} xl={12}>
                                        <div className="mb-0">
                                            <label className="form-label">{t("job.specialNeeds")}</label>
                                            <Controller
                                                name="specialNeeds"
                                                control={control}
                                                render={({ field }) => (
                                                    <textarea
                                                        className={`form-control ${errors.specialNeeds ? "is-invalid" : ""}`}
                                                        placeholder={t("job.enterSpecialNeeds")}
                                                        {...field}
                                                    />
                                                )}
                                            />
                                            {errors.specialNeeds &&
                                                <div className="invalid-feedback">
                                                    {t(errors.specialNeeds.message as string)}
                                                </div>}
                                        </div>
                                    </Col>

                                    {/* Only show location fields when locationType is not 'online' */}
                                    {locationType && locationType !== 'online' && (
                                        <>
                                            <Col sm={12} xl={secondLocationOptions.length > 0 ? 6 : 12}>
                                                <div className="mb-0">
                                                    <label className="form-label">{t("job.mainLocation")} <span className="text-danger">*</span></label>
                                                    <Select
                                                        id="mainLocation"
                                                        classNamePrefix="react-select"
                                                        className={`react-select-container ${errors.location ? 'is-invalid' : ''}`}
                                                        options={mainLocationList}
                                                        value={mainLocationList.find((loc) => loc.value === selectedMainLocation)}
                                                        onChange={(selectedOption: any) => {
                                                            if (selectedOption?.value !== selectedMainLocation) {
                                                                setSelectedMainLocation(selectedOption?.value || null);
                                                                setSecondLocationOptions([]);
                                                                if (errors.location) {
                                                                    setValue("location", undefined as any, { shouldValidate: true });
                                                                } else {
                                                                    setValue("location", undefined as any);
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </div>
                                                {errors.location &&
                                                    <div className="invalid-feedback d-block">
                                                        {t(errors.location.message as string)}
                                                    </div>}
                                            </Col>

                                            {secondLocationOptions.length > 0 && (
                                                <Col sm={12} xl={6}>
                                                    <div className="mb-0">
                                                        <label className="form-label">{t("job.subLocation")} <span className="text-danger">*</span></label>
                                                        <Controller
                                                            name="location"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Select
                                                                    id="subLocation"
                                                                    classNamePrefix="react-select"
                                                                    className={`react-select-container ${errors.location ? 'is-invalid' : ''}`}
                                                                    options={secondLocationOptions}
                                                                    value={secondLocationOptions.find((opt) => opt.value === field.value)}
                                                                    onChange={(selectedOption: any) => field.onChange(selectedOption?.value)}
                                                                    isOptionDisabled={(option) => option.isDisabled}
                                                                />
                                                            )}
                                                        />
                                                        {errors.location && <div
                                                            className="invalid-feedback d-block">
                                                            {t(errors.location.message as string)}
                                                        </div>}
                                                    </div>
                                                </Col>
                                            )}
                                        </>
                                    )}
                                    <Col sm={12} xl={secondDropdownOptions.length > 0 ? 4 : 12}>
                                        <div className="mb-0">
                                            <label className="form-label">{t("job.mainCategory")} ({t("job.level1")}) <span className="text-danger">*</span></label>
                                            <Select
                                                id="mainCategory"
                                                classNamePrefix="react-select"
                                                className={`react-select-container ${errors.category ? 'is-invalid' : ''}`}
                                                options={mainCategoryList}
                                                isClearable
                                                value={mainCategoryList.find(cat => cat.value === selectedMainCategory) || null}
                                                onChange={(selectedOption: any) => {
                                                    // When main category changes, reset dependent selects (second and third)
                                                    if (!selectedOption) {
                                                        setSelectedMainCategory(null);
                                                        setSecondDropdownOptions([]);
                                                        setThirdDropdownOptions([]);
                                                        setSelectedSecondCategory(null);
                                                        // Clear category form value when main is cleared
                                                        setValue("category", undefined as any, { shouldValidate: true });
                                                        return;
                                                    }

                                                    if (selectedOption?.value !== selectedMainCategory) {
                                                        const nestedDropdown = mainCategoryList?.find(category => category.value === selectedOption?.value)?.children;
                                                        setSelectedMainCategory(selectedOption?.value || null);
                                                        setSecondDropdownOptions(nestedDropdown?.map((category: any) => ({
                                                            label: category?.title,
                                                            value: category?.id,
                                                            children: category?.children
                                                        })) || []);
                                                        // Clear deeper selections
                                                        setSelectedSecondCategory(null);
                                                        setThirdDropdownOptions([]);
                                                        // Set form category to the main category id (main is now required)
                                                        setValue("category", selectedOption?.value, { shouldValidate: true });
                                                    }
                                                }}
                                            />
                                        </div>
                                        {errors.category &&
                                            <div className="invalid-feedback d-block">
                                                {t(errors.category.message as string)}
                                            </div>}
                                    </Col>
                                    {secondDropdownOptions.length > 0 && (
                                        <Col sm={12} xl={4}>
                                            <div className="mb-0">
                                                <label className="form-label">{t("job.subcategories")} ({t("job.level2")})</label>
                                                <Controller
                                                    name="category"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Select
                                                            id="subcategories"
                                                            classNamePrefix="react-select"
                                                            className={`react-select-container ${errors.category ? 'is-invalid' : ''}`}
                                                            options={secondDropdownOptions}
                                                            isClearable
                                                            value={secondDropdownOptions.find(opt => opt.value === selectedSecondCategory) || null}
                                                            onChange={(selectedOption: any) => {
                                                                if (!selectedOption) {
                                                                    // Clear second and third level when subcategory cleared
                                                                    // revert category form value back to main category (if present)
                                                                    const mainVal = selectedMainCategory || undefined;
                                                                    field.onChange(mainVal);
                                                                    setSelectedSecondCategory(null);
                                                                    setThirdDropdownOptions([]);
                                                                    setValue("category", mainVal as any, { shouldValidate: true });
                                                                    return;
                                                                }

                                                                const nestedDropdown = secondDropdownOptions
                                                                    ?.find(category => category.value === selectedOption?.value)?.children;
                                                                field.onChange(selectedOption?.value);
                                                                setSelectedSecondCategory(selectedOption?.value);
                                                                setThirdDropdownOptions(nestedDropdown?.map((category: any) => ({
                                                                    label: category?.title,
                                                                    value: category?.id
                                                                })) || []);
                                                                // set form category to selected subcategory id
                                                                setValue("category", selectedOption?.value, { shouldValidate: true });
                                                            }}
                                                            isOptionDisabled={(option) => option.isDisabled}
                                                        />
                                                    )}
                                                />
                                                {errors.category && <div
                                                    className="invalid-feedback d-block">
                                                    {t(errors.category.message as string)}
                                                </div>}
                                            </div>
                                        </Col>
                                    )}
                                    {
                                        !!thirdDropdownOptions?.length && !!secondDropdownOptions?.length &&
                                        <Col sm={12} xl={4}>
                                            <div className="mb-0">
                                                <label className="form-label">{t("job.category")} ({t("job.level3")})</label>
                                                <Controller
                                                    name="category"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Select
                                                            id="categories"
                                                            classNamePrefix="react-select"
                                                            className={`react-select-container ${errors.category ? 'is-invalid' : ''}`}
                                                            options={thirdDropdownOptions}
                                                            isClearable
                                                            value={thirdDropdownOptions.find(opt => opt.value === field.value) || null}
                                                            onChange={(selectedOption: any) => {
                                                                if (!selectedOption) {
                                                                    // Revert to subcategory or main category if available
                                                                    const fallback = selectedSecondCategory || selectedMainCategory || undefined;
                                                                    field.onChange(fallback);
                                                                    setValue("category", fallback as any, { shouldValidate: true });
                                                                    return;
                                                                }
                                                                field.onChange(selectedOption?.value);
                                                                setValue("category", selectedOption?.value, { shouldValidate: true });
                                                            }}
                                                            isOptionDisabled={(option) => option.isDisabled}
                                                        />
                                                    )}
                                                />
                                                {errors.category && <div
                                                    className="invalid-feedback d-block">
                                                    {t(errors.category.message as string)}
                                                </div>}
                                            </div>
                                        </Col>
                                    }
                                    <Col sm={12} xl={12}>
                                        <div>
                                            <label className="form-label">{t("job.description")} <span className="text-danger">*</span></label>
                                            <div className={errors.description ? 'is-invalid' : ''}>
                                                <Controller
                                                    name="description"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Editor
                                                            apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                                                            value={field.value}
                                                            onEditorChange={(content: string) => field.onChange(content)}
                                                            init={{
                                                                height: 360,
                                                                menubar: false,
                                                                branding: false,
                                                                plugins: [
                                                                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                                                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                                                    'insertdatetime', 'media', 'table', 'help', 'wordcount',
                                                                ],
                                                                toolbar:
                                                                    'undo redo | blocks | bold italic underline strikethrough | ' +
                                                                    'alignleft aligncenter alignright alignjustify | ' +
                                                                    'bullist numlist outdent indent | table | link image | removeformat | code',
                                                                content_style: 'body { font-family: Helvetica, Arial, sans-serif; font-size: 14px; }',
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            {errors.description &&
                                                <div className="invalid-feedback">
                                                    {t(errors.description.message as string)}
                                                </div>}
                                        </div>
                                    </Col>

                                    <hr className="mt-5" />
                                    <Col sm={12}>
                                        <h5 className=" mb-3">
                                            {t("job.lectureTimeSectionTitle")}
                                        </h5>
                                    </Col>
                                    <Col sm={12} xl={3}>
                                        <div className="mb-0">
                                            <label className="form-label">{t("job.lectureTimeWindowType")} <span className="text-danger">*</span></label>
                                            <Controller
                                                name="lectureTimeWindowType"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        id="lectureTimeWindowType"
                                                        classNamePrefix="react-select"
                                                        className={`react-select-container ${errors.lectureTimeWindowType ? "is-invalid" : ""}`}
                                                        value={lectureTimeWindowType.find((opt: any) => opt.value === field.value)}
                                                        onChange={(selectedOption) => field.onChange(selectedOption ? selectedOption.value : null)}
                                                        options={lectureTimeWindowType}
                                                    />
                                                )}
                                            />
                                            {errors.lectureTimeWindowType && <div
                                                className="invalid-feedback">
                                                {t(errors.lectureTimeWindowType.message as string)}
                                            </div>}
                                        </div>
                                    </Col>

                                    <>
                                        <Col sm={12} xl={3}>
                                            <div className="mb-3">
                                                <label className="mb-2">{t("lecture.fromDate")} <span className="text-danger">*</span></label>
                                                <Controller
                                                    name="startDate"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <DatePicker
                                                            selected={field.value}
                                                            onChange={field.onChange}
                                                            className={`form-control ${errors.startDate ? 'is-invalid' : ''}`}
                                                            dateFormat="yyyy-MM-dd"
                                                        />
                                                    )}
                                                />
                                                {errors.startDate && (
                                                    <div className="invalid-feedback d-block">
                                                        {t(errors.startDate.message as string)}
                                                    </div>
                                                )}
                                            </div>
                                        </Col>
                                        <Col sm={12} xl={3}>
                                            <div className="mb-3">
                                                <label className="mb-2">{t("lecture.toDate")} <span className="text-danger">*</span></label>
                                                <Controller
                                                    name="endDate"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <DatePicker
                                                            selected={field.value}
                                                            onChange={field.onChange}
                                                            className={`form-control ${errors.endDate ? 'is-invalid' : ''}`}
                                                            dateFormat="yyyy-MM-dd"
                                                        />
                                                    )}
                                                />
                                                {errors.endDate && (
                                                    <div className="invalid-feedback d-block">
                                                        {t(errors.endDate.message as string)}
                                                    </div>
                                                )}
                                            </div>

                                        </Col>
                                        {watch("lectureTimeWindowType") === "already_defined" && (
                                            <Col sm={12} xl={12}>
                                                <div className="mb-3">
                                                    <label>{t("lecture.additionalNotes")}</label>
                                                    <Controller
                                                        name="timeWindowDescription"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <textarea
                                                                className={`form-control ${errors.timeWindowDescription ? 'is-invalid' : ''}`}
                                                                rows={10}
                                                                placeholder={t("common.placeholder")}
                                                                {...field}
                                                            />
                                                        )}
                                                    />
                                                    {errors.timeWindowDescription && (
                                                        <div className="invalid-feedback d-block">
                                                            {t(errors.timeWindowDescription.message as string)}
                                                        </div>
                                                    )}
                                                </div>
                                            </Col>

                                        )}
                                    </>

                                    {watch("lectureTimeWindowType") === "self_selectable" && (
                                        <>


                                            {showFullCalendar && (
                                                <div>
                                                    {workingDays.map((day: any) => {
                                                        // Find the entry for this day in timeWindowDays
                                                        const dayObj = timeWindowDays.find((item: any) => item.day === day);

                                                        // Helper to convert "HH:mm" to Date object for DatePicker
                                                        const getTimeValue = (timeStr?: string) => {
                                                            if (!timeStr) return null;
                                                            const [h, m] = timeStr.split(":");
                                                            if (!h || !m) return null;
                                                            const d = new Date();
                                                            d.setHours(Number(h), Number(m), 0, 0);
                                                            return d;
                                                        };

                                                        return (
                                                            <div key={day} style={{ marginBottom: 16 }}>
                                                                <label>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!!dayObj}
                                                                        onChange={() => {
                                                                            if (dayObj) {
                                                                                // Remove from timeWindowDays
                                                                                setValue(
                                                                                    "timeWindowDays",
                                                                                    timeWindowDays.filter((item: any) => item.day !== day)
                                                                                );
                                                                            } else {
                                                                                // Add to timeWindowDays
                                                                                setValue("timeWindowDays", [
                                                                                    ...timeWindowDays,
                                                                                    { day, startTime: "", endTime: "" }
                                                                                ]);
                                                                            }
                                                                        }}
                                                                        style={{ marginRight: 8 }}
                                                                    />
                                                                    {day.charAt(0).toUpperCase() + day.slice(1)}
                                                                </label>
                                                                {!!dayObj && (
                                                                    <div className="row mt-2">
                                                                        <div className="col-3">
                                                                            <DatePicker
                                                                                selected={getTimeValue(dayObj.startTime)}
                                                                                onChange={date => {
                                                                                    setValue(
                                                                                        "timeWindowDays",
                                                                                        timeWindowDays.map((item: any) =>
                                                                                            item.day === day
                                                                                                ? {
                                                                                                    ...item,
                                                                                                    startTime: date ? moment(date).format("HH:mm") : "",
                                                                                                    day // ensure day is set
                                                                                                }
                                                                                                : item
                                                                                        )
                                                                                    );
                                                                                }}
                                                                                showTimeSelect
                                                                                showTimeSelectOnly
                                                                                timeIntervals={5}
                                                                                timeCaption="Start Time"
                                                                                dateFormat="HH:mm"
                                                                                placeholderText="From time"
                                                                                className="form-control"
                                                                            />
                                                                        </div>
                                                                        <div className="col-3">
                                                                            <DatePicker
                                                                                selected={getTimeValue(dayObj.endTime)}
                                                                                onChange={date => {
                                                                                    setValue(
                                                                                        "timeWindowDays",
                                                                                        timeWindowDays.map((item: any) =>
                                                                                            item.day === day
                                                                                                ? {
                                                                                                    ...item,
                                                                                                    endTime: date ? moment(date).format("HH:mm") : "",
                                                                                                    day
                                                                                                }
                                                                                                : item
                                                                                        )
                                                                                    );
                                                                                }}
                                                                                showTimeSelect
                                                                                showTimeSelectOnly
                                                                                timeIntervals={5}
                                                                                timeCaption="End Time"
                                                                                dateFormat="HH:mm"
                                                                                placeholderText="To time"
                                                                                className="form-control"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                        </>
                                    )}

                                    {/* 
                                    <div >
                                        <FullCalendars />
                                    </div> */}
                                    <Col sm={12}>
                                        <Button type="submit" variant="primary">
                                            {t(`job.${isEdit ? "update" : "create"}`)}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

        </React.Fragment>
    );
};

export default JobForm;